import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageSquareWarning, CreditCard, ArrowRight, Zap, Clock } from 'lucide-react';

const products = [
  {
    icon: MessageSquareWarning,
    tag: 'Live · Konsument-SaaS',
    tagColor: 'text-indigo-600 bg-indigo-50',
    statusDot: 'bg-emerald-400',
    statusText: 'Aktiv produkt',
    title: 'Klagobot',
    description:
      'Klagobot hjälper konsumenter att skriva effektiva klagomålsbrev till företag och myndigheter. AI:n guidar dig genom ärendet och formulerar ett professionellt brev på sekunder.',
    bullets: [
      'Skriv klagomål till företag, banker och försäkringsbolag',
      'AI-formulerade brev baserade på konsumentlagstiftning',
      'Enkel att använda — inga juridiska förkunskaper krävs',
    ],
    href: 'https://klagobot.se',
    external: true,
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.35)',
    border: 'hover:border-indigo-400/50',
    cta: 'Läs mer om Klagobot',
    ctaStyle: 'text-brand-indigo',
  },
  {
    icon: CreditCard,
    tag: 'Coming Soon',
    tagColor: 'text-emerald-700 bg-emerald-50',
    statusDot: 'bg-amber-400 animate-pulse',
    statusText: 'Under utveckling',
    title: 'Swishkassa',
    description:
      'En enkel betalningslösning för föreningar, event och kiosker — byggd kring Swish. Ta emot betalningar, hantera kvittenser och se överskotten direkt. Ingen dyr kortterminal.',
    bullets: [
      'Swish-betalningar med automatiska kvittenser',
      'Perfekt för föreningskassor, marknader och event',
      'Realtidsöversikt — se alltid vad som kommit in',
    ],
    href: '/swishkassa',
    gradient: 'from-emerald-400 to-teal-500',
    glow: 'rgba(52,211,153,0.3)',
    border: 'hover:border-emerald-400/40',
    cta: 'Anmäl intresse',
    ctaStyle: 'text-emerald-600',
  },
];

const SaaSProducts = () => {
  return (
    <section className="py-28 bg-surface-subtle relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-indigo-50 via-violet-50 to-transparent opacity-60 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">
            Våra produkter
          </span>
          <h2 className="text-4xl sm:text-[46px] font-extrabold text-text-primary tracking-[-0.02em] leading-[1.1] mb-5">
            SaaS-produkter som<br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
              löser riktiga problem
            </span>
          </h2>
          <p className="text-lg text-text-secondary max-w-lg mx-auto leading-relaxed">
            Utöver kundprojekt bygger vi egna produkter. Klagobot är live — fler är på väg.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {products.map((p, i) => (
            <motion.div
              key={p.title}
              whileHover={{ y: -5 }}
              className={`group relative bg-white rounded-2xl border border-border-light p-8 shadow-sm transition-all duration-300 ${p.border}`}
              style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 24px 64px ${p.glow}, 0 4px 20px rgba(0,0,0,0.06)`; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)'; }}
            >
              {/* Top row */}
              <div className="flex items-start justify-between mb-6">
                <div className={`inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br ${p.gradient} items-center justify-center shadow-lg`}
                  style={{ boxShadow: `0 8px 20px ${p.glow}` }}>
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${p.statusDot}`} />
                  <span className="text-[11px] font-semibold text-text-secondary">{p.statusText}</span>
                </div>
              </div>

              {/* Tag */}
              <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full mb-3 ${p.tagColor}`}>
                {p.tag}
              </span>

              {/* Title */}
              <h3 className="text-2xl font-extrabold text-text-primary mb-3 tracking-tight">{p.title}</h3>

              {/* Description */}
              <p className="text-[15px] text-text-secondary leading-relaxed mb-6">{p.description}</p>

              {/* Bullets */}
              <ul className="space-y-2 mb-8">
                {p.bullets.map(b => (
                  <li key={b} className="flex items-start gap-2 text-sm text-text-secondary">
                    <Zap className="w-3.5 h-3.5 text-brand-indigo mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {(p as { external?: boolean }).external ? (
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold ${p.ctaStyle} group-hover:opacity-80 transition-opacity`}
                >
                  {p.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </a>
              ) : (
                <Link
                  to={p.href}
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold ${p.ctaStyle} group-hover:opacity-80 transition-opacity`}
                >
                  {p.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
              )}

              {/* Gradient top accent on hover */}
              <div className={`absolute top-0 left-6 right-6 h-[2px] rounded-b-full bg-gradient-to-r ${p.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            </motion.div>
          ))}
        </div>

        {/* Coming soon teaser */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 text-sm text-text-secondary">
            <Clock className="w-4 h-4 text-text-muted" />
            Fler produkter är under development — följ oss på LinkedIn för uppdateringar
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default SaaSProducts;
